export const CHANNELS_SUPPORTING_BREAKDOWN = {
  channel: null,
  campaign: [
    "meta",
    "google",
    "tiktok",
    "email",
    "whatsapp",
    "msads",
    "pinterest",
    "snapchat",
    "outbrain",
    "taboola",
    "criteo",
    "organic",
  ],
  adset: ["meta", "google", "tiktok", "pinterest", "msads", "snapchat"],
  keyword: ["google"],
  asset_group: ["google"],
  ad: [
    "meta",
    "google",
    "tiktok",
    "pinterest",
    "msads",
    "outbrain",
    "taboola",
    "snapchat",
  ],
  creative: [
    "meta",
    "google",
    "tiktok",
    "pinterest",
    "msads",
    "outbrain",
    "taboola",
  ],
} as const;

export const CHANNELS_WITHOUT_BREAKDOWN_SUPPORT = ["direct"] as const;

export const AVAILABLE_MARKETING_CHANNEL_OPTIONS = [
  { label: "Meta", value: "facebook" },
  { label: "Google", value: "google" },
  { label: "TikTok", value: "tiktok" },
  { label: "Influencer", value: "influencer" },
  { label: "Email", value: "email" },
  { label: "WhatsApp", value: "whatsapp" },
  { label: "Microsoft Ads", value: "msads" },
  { label: "Pinterest", value: "pinterest" },
  { label: "Snapchat", value: "snapchat" },
  { label: "Taboola", value: "taboola" },
  { label: "Outbrain", value: "outbrain" },
  { label: "Criteo", value: "criteo" },
  { label: "Organic", value: "organic" },
  { label: "Direct", value: "direct" },
] as const;

export const TOUCHPOINT_OPTIONS = [
  { label: "Meta", value: "meta" },
  { label: "Google", value: "google" },
  { label: "TikTok", value: "tiktok" },
  { label: "Influencer", value: "influencer" },
  { label: "Email", value: "email" },
  { label: "WhatsApp", value: "whatsapp" },
  { label: "Microsoft Ads", value: "msads" },
  { label: "Pinterest", value: "pinterest" },
  { label: "Snapchat", value: "snapchat" },
  { label: "Taboola", value: "taboola" },
  { label: "Outbrain", value: "outbrain" },
  { label: "Criteo", value: "criteo" },
  { label: "Organic", value: "organic" },
  { label: "Direct", value: "direct" },
] as const;

export const KPI_METRIC_KEYS_LIST = [
  "ad_spend",
  "influencer_fixed_commission",
  "clicks",
  "link_click",
  "important_clicks",
  "impressions",
  "reach",
  "comment",
  "landing_page_view",
  "conversions",
  "all_conversions",
  "video_view",
  "onsite_conversion_post_save",
  "video_avg_time_watched_actions",
  "click_through_rate",
  "frequency",
  "thumbstop_ratio",
  "cost_per_click",
  "cost_per_mille",
  "customer_purchase",
  "customer_revenue",
  "customer_num_items",
  "customer_pageview",
  "customer_unique_pageview",
  "customer_productview",
  "customer_unique_productview",
  "customer_addtocart",
  "customer_unique_addtocart",
  "customer_checkout",
  "customer_unique_checkout",
  "influencer_variable_commission",
  "total_spend",
  "return_on_ad_spend",
  "cost_per_order",
  "conversion_rate",
  "average_order_value",
  "customer_acquisition_cost",
  "new_customer_purchase_ratio",
  "new_visitor_pageview_ratio",
] as const;

export const METRICS_LABELS: Record<string, string> = {
  ad_spend: "Ad Spend",
  influencer_fixed_commission: "Fixed Commissions",
  clicks: "Clicks",
  link_click: "Clicks (link)",
  important_clicks: "Important Clicks",
  impressions: "Impressions",
  reach: "Reach",
  comment: "Comments",
  landing_page_view: "Landing Page Views",
  conversions: "Conversions",
  all_conversions: "All Conversions",
  video_view: "Video Views",
  onsite_conversion_post_save: "Post Saves",
  video_avg_time_watched_actions: "Avg. Watch Time",
  click_through_rate: "CTR",
  frequency: "Frequency",
  thumbstop_ratio: "Thumbstop Ratio",
  cost_per_click: "CPC",
  cost_per_mille: "CPM",
  customer_purchase: "Orders",
  customer_revenue: "Revenue",
  customer_num_items: "Items Sold",
  customer_pageview: "Page Views",
  customer_unique_pageview: "Unique Page Views",
  customer_productview: "Content Views",
  customer_unique_productview: "Unique Content Views",
  customer_addtocart: "Adds to Cart",
  customer_unique_addtocart: "Unique Adds to Cart",
  customer_checkout: "Checkouts Initiated",
  customer_unique_checkout: "Unique Checkouts Initiated",
  influencer_variable_commission: "Variable Commissions",
  total_spend: "Ad Spend",
  return_on_ad_spend: "ROAS",
  cost_per_order: "CPO",
  conversion_rate: "Conversion Rate",
  average_order_value: "AOV",
  customer_acquisition_cost: "CAC",
  new_customer_purchase_ratio: "New Customer Rate",
  new_visitor_pageview_ratio: "New Visitor Rate",
};

export const INSIGHTS_METRIC_OPTIONS = [
  { value: "customer_revenue", label: "Revenue" },
  { value: "total_spend", label: "Ad Spend" },
  { value: "return_on_ad_spend", label: "ROAS" },
  { value: "customer_purchase", label: "Orders" },
  { value: "average_order_value", label: "AOV" },
  { value: "customer_acquisition_cost", label: "CAC" },
  { value: "cost_per_order", label: "CPO" },
  { value: "click_through_rate", label: "CTR" },
  { value: "cost_per_click", label: "CPC" },
  { value: "cost_per_mille", label: "CPM" },
  { value: "conversion_rate", label: "Conversion Rate" },
  { value: "new_customer_purchase_ratio", label: "New Customer Rate" },
  { value: "impressions", label: "Impressions" },
  { value: "clicks", label: "Clicks" },
] as const;

export type InsightsMetricValue =
  (typeof INSIGHTS_METRIC_OPTIONS)[number]["value"];

export const PRESET_STATUS_ENUM = {
  READY: "READY",
  REPROCESSING: "REPROCESSING",
  PROCESSING: "PROCESSING",
  REGISTERED: "REGISTERED",
  PREFILLING: "PREFILLING",
} as const;

export type PRESET_STATUS_ENUM =
  (typeof PRESET_STATUS_ENUM)[keyof typeof PRESET_STATUS_ENUM];
